import React, {useEffect, useState} from 'react';
import {IShippingIntervalOptions, IShopifyData, ISimpleSubProduct} from '../types';
import getCommonShippingInterval from '../utils/getCommonShippingInterval';
import getRcDiscountedPrice from '../utils/getRcDiscountedPrice';
import {STEPS} from '../constants';
import SimpleSubInfo from './SimpleSubInfo';
import SimpleSubMain from './SimpleSubMain';
import SimpleSubSummary from './SimpleSubSummary';
import SimpleSubBar from './SimpleSubBar';
import SubscriptionService from '../../../../../scripts/vendor/shared/subscription/subscriptionService';

interface SimpleSubBuilderProps {
  shopifyData: IShopifyData;
  subService: SubscriptionService;
}

const SimpleSubBuilder: React.FunctionComponent<SimpleSubBuilderProps> = ({shopifyData, subService}: SimpleSubBuilderProps) => {
  const [step, setStep] = useState<string>(STEPS[0]);
  const [loading, setLoading] = useState<boolean>(true);
  const [simpleSubProducts, setSimpleSubProducts] = useState<ISimpleSubProduct[]>(null);
  const [shippingIntervalOptions, setShippingIntervalOptions] = useState<IShippingIntervalOptions>(null);

  useEffect((): void => {
    if (shopifyData.products.length < 1) {
      alert('Please make sure all selected products have their shipping options set up in Recharge!');
    }

    setSimpleSubProducts(shopifyData.products);

    const commonOptions: IShippingIntervalOptions = getCommonShippingInterval(shopifyData.products);
    setShippingIntervalOptions(commonOptions);

    setLoading(false);
  }, []);

  const addedProducts: ISimpleSubProduct[] = simpleSubProducts?.filter(prod => prod.numAdded > 0);

  const barShouldDisplay: boolean = addedProducts?.length > 0 && step && step !== 'summary';

  // This is for hiding the Smile UI button, etc. Can't be done in CSS only because of iframes and ridiculous z-indexes.
  if (barShouldDisplay) {
    document.body.classList.add('simple-sub-bar--visible');
  } else {
    document.body.classList.remove('simple-sub-bar--visible');
  }

  const addedTotal: number = addedProducts?.reduce((acc, curr) => {
    let price: number = curr.price;

    const discountedPrice: number = getRcDiscountedPrice(curr);

    if (discountedPrice) {
      price = discountedPrice;
    }

    return acc + (price * curr.numAdded);
  }, 0);

  if (loading) {
    return <p className="loading-widget">...</p>;
  }

  return (
    <div className='simple-sub'>
      <div className="container simple-sub-inner">
        <SimpleSubInfo shopifyData={shopifyData}/>

        {step === 'build' && (
          <SimpleSubMain
            langStrings={shopifyData.langStrings}
            shippingIntervalOptions={shippingIntervalOptions}
            setShippingIntervalOptions={setShippingIntervalOptions}
            simpleSubProducts={simpleSubProducts}
            setSimpleSubProducts={setSimpleSubProducts}
            shopifyData={shopifyData}
          />
        )}

        {step === 'summary' && (
          <SimpleSubSummary
            langStrings={shopifyData.langStrings}
            addedProducts={addedProducts}
            addedTotal={addedTotal}
            shippingIntervalOptions={shippingIntervalOptions}
            shopifyData={shopifyData}
            subService={subService}
          />
        )}
      </div>

      {barShouldDisplay && (
        <SimpleSubBar
          langStrings={shopifyData.langStrings}
          addedTotal={addedTotal}
          step={step}
          setStep={setStep}
        />
      )}
    </div>
  );
};

export default SimpleSubBuilder;